<script lang="ts">
  import { tick } from 'svelte';

  import DropdownList from '$shared/atoms/DropdownList/DropdownList.svelte';
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import GlobalListHOC from '$shared/molecules/GlobalListHOC/GlobalListHOC.svelte';
  import type { DropdownListOption } from '$shared/components/types';

  const DefaultLabel = 'Please Select';

  interface Props {
    list?: any;
    loading?: boolean;
    disabled?: boolean;
    label?: any;
    name?: string;
    selectable?: boolean;
    islarge?: boolean;
    maxWidth?: boolean;
    hasOverflow?: boolean;
    listOption?: DropdownListOption;
    onchange?: (detail: CommonList) => void;
    oncreate?: (value: string) => void;
    oninputcreate?: (value: string) => void;
  }

  let {
    list = [],
    loading = false,
    disabled = false,
    label = $bindable(''),
    name = '',
    selectable = false,
    islarge = false,
    maxWidth = false,
    hasOverflow = false,
    listOption = {},
    onchange,
    oncreate,
    oninputcreate
  }: Props = $props();

  let parent: HTMLElement = $state();
  let appWithList: GlobalListHOC = $state();
  let active = $state(false);
  let listElement: DropdownList = $state();

  const iconColor = $derived(disabled ? 'white' : active ? 'active' : 'grey');

  const onClickLabel = (): void => {
    active = !active;
    tick().then(() => {
      if (!active) return;

      appWithList?.setListPosition();
      listElement?.focus();
    });
  };

  const onClickWindow = (e: any): void => {
    if (parent && !parent.contains(e.target)) {
      active = false;
    }
  };

  const onClickList = (detail: CommonList): void => {
    active = false;
    if (label === detail.label) {
      return;
    }
    label = detail.label;
    onchange?.(detail);
  };

  const onCreateItem = (value: string): void => {
    active = false;
    label = value;
    oncreate?.(value);
  };

  const onCreateInputItem = (value: string): void => {
    label = value;
    oninputcreate?.(value);
  };
</script>

<svelte:window onmouseup={onClickWindow} />

{#snippet labelSnippet()}
  <div class="label-button">
    <button
      type="button"
      data-cy={name}
      disabled={disabled || loading}
      class="label"
      class:active
      class:islarge
      onclick={onClickLabel}
    >
      <div class="text">{label || DefaultLabel}</div>
      <div class="icon">
        {#if loading}
          <Spinner light />
        {:else}
          <Icon value="icon-chevron-down {iconColor}" />
        {/if}
      </div>
    </button>
  </div>
{/snippet}

{#snippet listSnippet()}
  <div class="list" class:transform={hasOverflow}>
    <DropdownList
      option={listOption}
      {list}
      bind:this={listElement}
      onclick={onClickList}
      oncreate={onCreateItem}
      oninputcreate={onCreateInputItem}
    />
  </div>
{/snippet}

<div bind:this={parent} class="comp-dropdown" class:maxWidth data-cy="dropdown">
  <GlobalListHOC
    bind:this={appWithList}
    show={active}
    {hasOverflow}
    label={labelSnippet}
    list={listSnippet}
  />
</div>

<style>
  .comp-dropdown,
  .label,
  .label .icon {
    display: flex;
    align-items: center;
  }
  .comp-dropdown {
    display: inline-flex;
    position: relative;
  }
  .comp-dropdown.maxWidth,
  .maxWidth .label {
    width: 100%;
  }
  .label {
    min-width: 110px;
    height: 30px;
    padding: 0 12px;
    justify-content: space-between;
    font-size: 14px;
    line-height: 22px;
    border: 1px solid #e8eaef;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    background-color: #fff;
    color: var(--grey-dark);
  }
  .label.islarge {
    height: 36px;
  }
  .label:hover {
    background-color: var(--grey-bg);
  }
  .label:disabled {
    background-color: var(--grey-light);
    color: #fff;
    cursor: not-allowed;
  }
  .label.active {
    background-color: var(--light-blue);
  }
  .label.active {
    background-color: var(--light-blue);
    color: var(--blue);
    border-color: var(--light-blue);
  }
  .icon {
    height: 100%;
    margin-left: 8px;
  }
  .list {
    min-width: 100%;
    position: absolute;
    top: 100%;
    left: 0;
    /* min-width: 200px; */
    transform: translate(0, 8px);
    z-index: 99;
  }
  .list.transform {
    transform: none;
  }
  .text {
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
  }
</style>
